import { memo } from 'react';
import type { FC, ReactNode } from 'react';

import cn from 'classnames';

import { useViewportType } from '../../utils/useViewportType';
import { Description } from '../Description/Description';
import type { ImageSources } from '../../../helpers/images';
import { Image } from '../../Image/Image';
import { Label } from '../../Label/Label';

import * as styles from './SplashContent.module.scss';

interface SplashContentBaseProps {
  title?: string;
  description?: string | null;
  subtitle?: ReactNode;
  buttons?: ReactNode | ReactNode[];
  buttonsHelperText?: ReactNode | ReactNode[];
  badge?: ReactNode;
  verticalImageSources?: ImageSources;
  variant?: 'base' | 'carousel';
  labels?: ReactNode[];
  className?: string;
}

const SplashContentBase: FC<SplashContentBaseProps> = ({
  title,
  description,
  subtitle,
  buttons,
  buttonsHelperText,
  badge,
  verticalImageSources,
  variant = 'base',
  labels,
  className,
}) => {
  const { isMobile } = useViewportType();
  const verticalPosterTag = variant !== 'carousel' &&
    isMobile &&
    verticalImageSources && (
      <div className={styles.imageContainer}>
        <Image
          className={styles.verticalImage}
          altText={title}
          imageSources={verticalImageSources}
        />
      </div>
    );

  return (
    <div
      className={cn(styles.container, className, {
        [styles.withPoster]: variant !== 'carousel' && isMobile,
      })}
    >
      {verticalPosterTag}
      {Boolean(badge) && <div className={styles.badge}>{badge}</div>}
      <h1 className={styles.title}>{title}</h1>
      <h2 className={styles.subtitle}>{subtitle}</h2>
      {Boolean(buttons) && (
        <>
          <div className={styles.buttons}>{buttons}</div>
          {Boolean(buttonsHelperText) && (
            <div className={styles.buttonsHelperText}>{buttonsHelperText}</div>
          )}
        </>
      )}
      <Description className={styles.description} description={description} />
      <Label className={styles.labels} labels={labels} />
    </div>
  );
};

export const SplashContent = memo(SplashContentBase);
